import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';

type S = {
  user: User | null;
  session: Session | null;
  ready: boolean;
  // Legacy fields retained for compatibility with callers expecting entitlement/defaultProjectId/isLoading/error
  entitlement: any | null;
  defaultProjectId: string | null;
  isLoading: boolean;
  error: string | null;
  setFromSession: (s: Session | null) => void;
  setReady: (r: boolean) => void;
  awaitAuthReady: () => Promise<void>;
  // Legacy-style helpers kept to minimize churn
  setSession: (legacySession: any | null) => void;
  signOut: () => Promise<void>;
};

export const useAuthStore = create<S>((set, get) => {
  let readyResolve: (() => void) | null = null;
  const readyPromise = new Promise<void>((res) => (readyResolve = res));

  return {
    user: null,
    session: null,
    ready: false,
    entitlement: null,
    defaultProjectId: null,
    isLoading: true,
    error: null,
    setFromSession: (session) => {
      set({ session, user: session?.user ?? null, isLoading: false });
      try {
        // Best-effort: tag Sentry with hashed user id (no PII)
        const userId = session?.user?.id || null;
        if (userId) {
          try { (window as any).__lumiUserId = String(userId); } catch {}
          try {
            const metaName = 'lumiplot-user-id';
            let m = document.querySelector(`meta[name="${metaName}"]`) as HTMLMetaElement | null;
            if (!m) { m = document.createElement('meta'); m.setAttribute('name', metaName); document.head.appendChild(m); }
            m.setAttribute('content', String(userId));
          } catch {}
          import('../sentry').then(({ setSentryContextFromRaw, setSentryUser }) => {
            setSentryContextFromRaw({ userId });
            try { setSentryUser({ id: userId, email: (session?.user as any)?.email || null }); } catch {}
          }).catch(() => {});
          // Mixpanel identity: alias then identify (only if consent given)
          import('@/lib/consent').then(({ hasAnalyticsConsent }) => {
            if (!hasAnalyticsConsent()) return;
            import('@/lib/analytics').then(({ initAnalytics, track }) => {
              try { initAnalytics(); } catch {}
              import('@/analytics/mixpanel').then(({ alias, identify }) => {
            try { alias(String(userId)); } catch {}
            try { identify(String(userId), { email: (session?.user as any)?.email || undefined }); } catch {}
              }).catch(() => {});
            try {
              const url = new URL(window.location.href);
              const utm: any = {
                utm_source: url.searchParams.get('utm_source') || undefined,
                utm_medium: url.searchParams.get('utm_medium') || undefined,
                utm_campaign: url.searchParams.get('utm_campaign') || undefined,
                utm_term: url.searchParams.get('utm_term') || undefined,
                utm_content: url.searchParams.get('utm_content') || undefined,
              };
                track('page:viewed', { url_path: window.location.pathname, referrer_host: document.referrer ? new URL(document.referrer).host : null, ...utm } as any);
            } catch {}
            }).catch(() => {});
          }).catch(() => {});
          // First-time signup funnel event (best-effort)
          try {
            const u: any = session?.user || {};
            const createdAtStr = (u as any)?.created_at || (u as any)?.createdAt || null;
            const createdAt = createdAtStr ? new Date(createdAtStr).getTime() : 0;
            const ageMs = createdAt ? (Date.now() - createdAt) : Number.POSITIVE_INFINITY;
            const provider = (u?.app_metadata?.provider || 'email') as string;
            const key = `lumiplot:signup_tracked:${userId}`;
            const notYetTracked = !localStorage.getItem(key);
            // Treat newly created accounts in last 10 minutes as signups
            if (notYetTracked && ageMs >= 0 && ageMs < 10 * 60 * 1000) {
              import('@/lib/consent').then(({ hasAnalyticsConsent }) => {
                if (!hasAnalyticsConsent()) return;
                import('@/lib/analytics').then(({ track }) => {
                let ref: any = {};
                try { ref = JSON.parse(localStorage.getItem('lumi_utm') || '{}'); } catch {}
                const auth_provider = ['email','google','github'].includes(String(provider)) ? (provider as any) : 'other';
                try { track('user:signup', { auth_provider, referrer_host: ref.referrer_host || null, utm_source: ref.utm_source, utm_medium: ref.utm_medium, utm_campaign: ref.utm_campaign, utm_term: ref.utm_term, utm_content: ref.utm_content } as any); } catch {}
                try { localStorage.setItem(key, '1'); } catch {}
                }).catch(() => {});
              }).catch(() => {});
            }
          } catch {}
        }
      } catch {}
    },
    setReady: (r) => {
      set({ ready: r });
      if (r && readyResolve) readyResolve();
    },
    awaitAuthReady: async () => {
      if (get().ready) return;
      await readyPromise;
    },
    // Legacy setter: store server payload only in compatibility fields; do not overwrite Supabase session/user
    setSession: (legacySession) => {
      set({
        entitlement: legacySession?.entitlement ?? null,
        defaultProjectId: legacySession?.defaultProjectId ?? null,
        isLoading: false,
      });
    },
    signOut: async () => {
      try {
        const { signOut } = await import('@/lib/auth');
        await signOut();
        try { (await import('../sentry')).clearSentryContext(); } catch {}
      } catch (err) {
        set({ error: 'Failed to sign out' });
      }
    },
  };
});
